import { httpClient } from "../../../lib/http/axios";
import {
  mapBankCardCollectionOverviewPayload,
  mapBankCardCollectionResolvePayload,
  mapCardPaymentEReceiptApplyResult,
  mapCardPaymentEReceiptDownloadResult,
  mapCardPaymentEReceiptItem,
  mapCardPaymentOverviewPayload,
  mapCardPaymentRecordItem,
  mapPaymentRecordDetail,
  mapQrCollectionResolvePayload,
  mapRefundRecord,
  type BankCardCollectionOverviewResponseRaw,
  type BankCardCollectionResolveResponseRaw,
  type CardPaymentEReceiptApplyResponseRaw,
  type CardPaymentEReceiptDownloadResponseRaw,
  type CardPaymentEReceiptQueryResponseRaw,
  type CardPaymentOverviewResponseRaw,
  type CreateCardPaymentAccountResponseRaw,
  type CreateCardPaymentTransferResponseRaw,
  type CreateRefundResponseRaw,
  type PaymentRecordDetailResponseRaw,
  type QrCollectionResolveResponseRaw,
} from "../api/adapters";
import type {
  BankCardCollectionOverviewPayload,
  BankCardCollectionResolvePayload,
  CardPaymentAccountItem,
  CardPaymentEReceiptApplyResult,
  CardPaymentEReceiptDownloadResult,
  CardPaymentEReceiptItem,
  CardPaymentOverviewPayload,
  CardPaymentRecordItem,
  PaymentRecordDetail,
  QrCollectionResolvePayload,
  RefundRecord,
} from "../types";

/** 按团编号复用或创建二维码收款单。 */
export async function resolveQrCollection(
  teamId: string,
  page: number,
  pageSize: number,
): Promise<QrCollectionResolvePayload> {
  const response = await httpClient.post<QrCollectionResolveResponseRaw>(
    "/payment-validation/qr-collections/resolve",
    { team_id: teamId },
    { params: { page, page_size: pageSize } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("二维码收款接口返回了无效数据");
  }
  return mapQrCollectionResolvePayload(response.data.data);
}

/** 查询银联并同步已有二维码收款单及付款记录。 */
export async function refreshQrCollection(
  teamId: string,
  page: number,
  pageSize: number,
): Promise<QrCollectionResolvePayload> {
  const response = await httpClient.post<QrCollectionResolveResponseRaw>(
    "/payment-validation/qr-collections/refresh",
    { team_id: teamId },
    { params: { page, page_size: pageSize } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("二维码付款记录刷新接口返回了无效数据");
  }
  return mapQrCollectionResolvePayload(response.data.data);
}

/** 按团编号复用或创建中信单位结算卡开卡申请。 */
export async function resolveBankCardCollection(
  teamId: string,
  page: number,
  pageSize: number,
): Promise<BankCardCollectionResolvePayload> {
  const response = await httpClient.post<BankCardCollectionResolveResponseRaw>(
    "/payment-validation/bank-card-collections/resolve",
    { team_id: teamId },
    { params: { page, page_size: pageSize } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("银行卡号收款接口返回了无效数据");
  }
  return mapBankCardCollectionResolvePayload(response.data.data);
}

/** 轮询开卡进度或入账明细，同步银行卡号收款单。 */
export async function refreshBankCardCollection(
  teamId: string,
  page: number,
  pageSize: number,
): Promise<BankCardCollectionResolvePayload> {
  const response = await httpClient.post<BankCardCollectionResolveResponseRaw>(
    "/payment-validation/bank-card-collections/refresh",
    { team_id: teamId },
    { params: { page, page_size: pageSize } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("银行卡号收款刷新接口返回了无效数据");
  }
  return mapBankCardCollectionResolvePayload(response.data.data);
}

/** 查询固定中信结算卡账户总览与入账明细（兼容入口）。 */
export async function fetchBankCardCollectionOverview(
  page: number,
  pageSize: number,
): Promise<BankCardCollectionOverviewPayload> {
  const response = await httpClient.get<BankCardCollectionOverviewResponseRaw>(
    "/payment-validation/bank-card-collections/overview",
    { params: { page, page_size: pageSize } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("银行卡号收款总览接口返回了无效数据");
  }
  return mapBankCardCollectionOverviewPayload(response.data.data);
}

/** 查询卡支付付款账户总览与付款记录。 */
export async function fetchCardPaymentOverview(
  page: number,
  pageSize: number,
  payAccountNo?: string,
): Promise<CardPaymentOverviewPayload> {
  const response = await httpClient.get<CardPaymentOverviewResponseRaw>(
    "/payment-validation/card-payments/overview",
    {
      params: {
        page,
        page_size: pageSize,
        ...(payAccountNo ? { pay_account_no: payAccountNo } : {}),
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("卡支付总览接口返回了无效数据");
  }
  return mapCardPaymentOverviewPayload(response.data.data);
}

/** 新增卡支付付款账户。 */
export async function createCardPaymentAccount(input: {
  accountNo: string;
  accountName: string;
  bankName: string;
  openBranchName: string;
}): Promise<CardPaymentAccountItem> {
  const response = await httpClient.post<CreateCardPaymentAccountResponseRaw>(
    "/payment-validation/card-payments/accounts",
    {
      account_no: input.accountNo,
      account_name: input.accountName,
      bank_name: input.bankName,
      open_branch_name: input.openBranchName,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("新增付款账户接口返回了无效数据");
  }
  return response.data.data;
}

/** 提交卡支付转账。 */
export async function createCardPaymentTransfer(input: {
  amount: number;
  payAccountNo: string;
  receiveAccountNo: string;
  receiveAccountName: string;
  receiveOpenBankName: string;
  payAccountType: "00" | "01";
}): Promise<CardPaymentRecordItem> {
  const response = await httpClient.post<CreateCardPaymentTransferResponseRaw>(
    "/payment-validation/card-payments/transfers",
    {
      amount: input.amount,
      pay_account_no: input.payAccountNo,
      receive_account_no: input.receiveAccountNo,
      receive_account_name: input.receiveAccountName,
      receive_open_bank_name: input.receiveOpenBankName,
      pay_account_type: input.payAccountType,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("卡支付转账接口返回了无效数据");
  }
  return mapCardPaymentRecordItem(response.data.data);
}

/** 申请卡支付电子回单。 */
export async function applyCardPaymentEReceipt(
  paymentId: string,
): Promise<CardPaymentEReceiptApplyResult> {
  const response = await httpClient.post<CardPaymentEReceiptApplyResponseRaw>(
    `/payment-validation/card-payments/payments/${encodeURIComponent(paymentId)}/e-receipts/apply`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("电子回单申请接口返回了无效数据");
  }
  return mapCardPaymentEReceiptApplyResult(response.data.data);
}

/** 查询卡支付电子回单列表。 */
export async function queryCardPaymentEReceipt(
  paymentId: string,
): Promise<CardPaymentEReceiptItem[]> {
  const response = await httpClient.post<CardPaymentEReceiptQueryResponseRaw>(
    `/payment-validation/card-payments/payments/${encodeURIComponent(paymentId)}/e-receipts/query`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("电子回单查询接口返回了无效数据");
  }
  return (response.data.data.items ?? []).map(mapCardPaymentEReceiptItem);
}

/** 下载卡支付电子回单文件。 */
export async function downloadCardPaymentEReceipt(
  paymentId: string,
  rcptNum: string,
): Promise<CardPaymentEReceiptDownloadResult> {
  const response = await httpClient.post<CardPaymentEReceiptDownloadResponseRaw>(
    `/payment-validation/card-payments/payments/${encodeURIComponent(paymentId)}/e-receipts/download`,
    { rcpt_num: rcptNum },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("电子回单下载接口返回了无效数据");
  }
  return mapCardPaymentEReceiptDownloadResult(response.data.data);
}

/** 查询付款记录详情。 */
export async function fetchPaymentDetail(paymentId: string): Promise<PaymentRecordDetail> {
  const response = await httpClient.get<PaymentRecordDetailResponseRaw>(
    `/payment-validation/payments/${encodeURIComponent(paymentId)}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("付款详情接口返回了无效数据");
  }
  return mapPaymentRecordDetail(response.data.data);
}

/** 为付款记录提交退款。 */
export async function createPaymentRefund(
  paymentId: string,
  amount: number,
): Promise<RefundRecord> {
  const response = await httpClient.post<CreateRefundResponseRaw>(
    `/payment-validation/payments/${encodeURIComponent(paymentId)}/refunds`,
    { amount },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("退款接口返回了无效数据");
  }
  return mapRefundRecord(response.data.data);
}
